import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SaveBar } from '@shopify/app-bridge-react';
import { Modal } from '@shopify/polaris';
import { useBlocker } from 'react-router';

export type ButtonState = 'loading' | 'disabled' | 'default';

export interface ContextualBarActions {
  save: ButtonState;
  discard: ButtonState;
}

export type ContextualSaveBarAction = 'save' | 'discard' | 'hide' | 'show';

export type Subscription = {
  action: ContextualSaveBarAction;
  callback: () => void;
};

interface ContextualSaveBarContextProps {
  trigger: (action: ContextualSaveBarAction) => void;
  subscribe: (
    action: ContextualSaveBarAction,
    callback: () => void
  ) => () => void;
  set: (state: ContextualBarActions) => void;
}

// App Bridge requires a unique id for the save bar element.
const SAVE_BAR_ID = 'verisart-contextual-save-bar';

export const ContextualSaveBarContext =
  React.createContext<ContextualSaveBarContextProps>({
    trigger: () => {},
    subscribe: () => {
      return () => {};
    },
    set: () => {},
  });

export const useContextualSaveBar = () => {
  const contextualSaveBarContext = React.useContext(ContextualSaveBarContext);
  if (!contextualSaveBarContext) {
    throw new Error(
      'useContextualSaveBar must be used within a ContextualSaveBarContextProvider'
    );
  }
  return contextualSaveBarContext;
};

interface ContextualSaveBarProps {
  children: React.ReactNode;
}

export const ContextualSaveBar: React.FC<ContextualSaveBarProps> = ({
  children,
}) => {
  const [isContextualBarShown, setContextualBarVisibility] =
    useState<boolean>(false);
  const [actionsState, setActionsState] = useState<ContextualBarActions>({
    save: 'default',
    discard: 'default',
  });
  const subscriptionsRef = useRef<Subscription[]>([]);

  const trigger = useCallback((action: ContextualSaveBarAction) => {
    subscriptionsRef.current
      .filter((sub) => sub.action === action)
      .forEach((sub) => sub.callback());

    switch (action) {
      case 'show':
        setContextualBarVisibility(true);
        break;
      case 'hide':
        setContextualBarVisibility(false);
        break;
      default:
        break;
    }
  }, []);
  const subscribe = useCallback(
    (action: ContextualSaveBarAction, callback: () => void) => {
      const newSubscription = { action, callback };
      subscriptionsRef.current.push(newSubscription);

      // Return the unsubscribe function
      return () => {
        subscriptionsRef.current = subscriptionsRef.current.filter(
          (sub) => sub !== newSubscription
        );
      };
    },
    []
  );

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isContextualBarShown && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (!isContextualBarShown && blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [isContextualBarShown, blocker]);

  return (
    <ContextualSaveBarContext.Provider
      value={{
        trigger,
        subscribe,
        set: setActionsState,
      }}
    >
      <SaveBar id={SAVE_BAR_ID} open={isContextualBarShown}>
        <button
          variant="primary"
          loading={actionsState.save === 'loading'}
          disabled={actionsState.save === 'disabled'}
          onClick={() => trigger('save')}
        >
          Save
        </button>
        <button
          loading={actionsState.discard === 'loading'}
          disabled={actionsState.discard === 'disabled'}
          onClick={() => trigger('discard')}
        >
          Discard
        </button>
      </SaveBar>
      {/*
        The in-app navigation modal reuses the same Save / Discard actions as
        the SaveBar. After the bar hides (save/discard completed), the effect
        above calls blocker.proceed() to continue the blocked navigation.
      */}
      {blocker.state === 'blocked' && (
        <Modal
          open
          title="Unsaved changes"
          onClose={() => blocker.reset()}
          primaryAction={{
            content: 'Save',
            onAction: () => trigger('save'),
            loading: actionsState.save === 'loading',
          }}
          secondaryActions={[
            {
              content: 'Discard',
              destructive: true,
              onAction: () => trigger('discard'),
              disabled: actionsState.save === 'loading',
            },
            {
              content: 'Stay on page',
              onAction: () => blocker.reset(),
              disabled: actionsState.save === 'loading',
            },
          ]}
        >
          <Modal.Section>
            <p>
              You have unsaved changes on this page. Save them before leaving,
              or discard them and continue.
            </p>
          </Modal.Section>
        </Modal>
      )}
      {children}
    </ContextualSaveBarContext.Provider>
  );
};
